export const ANIMATION_DURATIONS = {
  // Element-specific durations
  ELEMENT: {
    CAROUSEL: {
      SLIDE_CHANGE: 0.2,
    },
    CIRCLE_ANIMATION: {
      DESKTOP: 4,
      MOBILE: 15,
    },
    HOVER: {
      DEFAULT: 0.6,
      DELAY: 0.1,
      TITLE: 0.05,
    },
    MARQUEE: 10,
    NAVIGATION: {
      DROPDOWN: 0.3,
      SLIDE: 0.6,
    },
    QUICK_VIEW: {
      FADE_IN_MOVE_UP: 0.4,
      FADE_OUT_MOVE_DOWN: 0.2,
    },
    SCROLL_BAR: 0.8,
  },

  // Section-specific durations
  SECTION: {
    FADE_IN: 0.8,
    STAGGER_ITEM: 0.2,
  },
};

export const ANIMATION_EASES = {
  POWER3_IN_OUT: 'power3.inOut',
  POWER3_OUT: 'power3.out',
  SINE_IN: 'sine.in',
  SINE_OUT: 'sine.out',
};

// Border radius values for animations
export const BORDER_RADIUS = {
  DEFAULT: '20px',
  PILL: '250px',
};

export const DESKTOP_CIRCLE_POSITIONS = [
  { end: 0.5, start: 0.33 },
  { end: 0.08, start: -0.165 },
  { end: -0.1, start: -0.25 },
];

/**
 * Common animation configurations for reuse across sections
 * These can be applied directly to GSAP tween methods or spread into custom configs
 */
export const ANIMATION_CONFIGS = {
  /**
   * Border radius pill animation
   * @example tl.to(elements, ANIMATION_CONFIGS.BORDER_RADIUS_PILL, 0);
   */
  BORDER_RADIUS_PILL: {
    borderRadius: BORDER_RADIUS.PILL,
    duration: ANIMATION_DURATIONS.ELEMENT.HOVER.DEFAULT,
    ease: ANIMATION_EASES.POWER3_IN_OUT,
  },

  /**
   * Carousel fade in animation
   * @example tl.to(element, ANIMATION_CONFIGS.CAROUSEL_FADE_IN, 0);
   */
  CAROUSEL_FADE_IN: {
    duration: ANIMATION_DURATIONS.ELEMENT.CAROUSEL.SLIDE_CHANGE,
    ease: ANIMATION_EASES.SINE_OUT,
    opacity: 1,
  },

  /**
   * Carousel fade out animation
   * @example tl.to(element, ANIMATION_CONFIGS.CAROUSEL_FADE_OUT, 0);
   */
  CAROUSEL_FADE_OUT: {
    duration: ANIMATION_DURATIONS.ELEMENT.CAROUSEL.SLIDE_CHANGE,
    ease: ANIMATION_EASES.SINE_IN,
    opacity: 0,
  },

  /**
   * Standard fade-in animation
   * @example tl.to(element, ANIMATION_CONFIGS.FADE_IN, 0);
   */
  FADE_IN: {
    duration: ANIMATION_DURATIONS.SECTION.FADE_IN,
    ease: ANIMATION_EASES.SINE_OUT,
    opacity: 1,
  },

  /**
   * Fade in cta buttons animation
   * @example tl.to(element, ANIMATION_CONFIGS.FADE_IN_CTA, 0);
   */
  FADE_IN_CTA: {
    duration: ANIMATION_DURATIONS.ELEMENT.HOVER.DEFAULT,
    ease: ANIMATION_EASES.SINE_OUT,
    opacity: 1,
  },

  /**
   * Fade in image animation
   * @example tl.to(element, ANIMATION_CONFIGS.FADE_IN_IMAGE, 0);
   */
  FADE_IN_IMAGE: {
    duration: ANIMATION_DURATIONS.ELEMENT.HOVER.DEFAULT,
    ease: ANIMATION_EASES.SINE_OUT,
    opacity: 1,
  },

  /**
   * Fade out border opacity animation
   * @example tl.to(element, ANIMATION_CONFIGS.FADE_OUT_BORDER_OPACITY, 0);
   */
  FADE_OUT_BORDER_OPACITY: {
    borderColor: 'transparent',
    duration: ANIMATION_DURATIONS.ELEMENT.HOVER.DEFAULT * 0.5,
    ease: ANIMATION_EASES.SINE_IN,
  },

  /**
   * Fade out cta buttons animation
   * @example tl.to(element, ANIMATION_CONFIGS.FADE_OUT_CTA, 0);
   */
  FADE_OUT_CTA: {
    duration: ANIMATION_DURATIONS.ELEMENT.HOVER.DEFAULT,
    ease: ANIMATION_EASES.SINE_IN,
    opacity: 0,
  },
  /**
   * Fade out title animation
   * @example tl.to(element, ANIMATION_CONFIGS.FADE_OUT_TITLE, 0);
   */
  FADE_OUT_TITLE: {
    duration: ANIMATION_DURATIONS.ELEMENT.HOVER.TITLE,
    ease: ANIMATION_EASES.SINE_IN,
    opacity: 0,
  },

  /**
   * Standard move-up animation
   * @example tl.to(element, ANIMATION_CONFIGS.MOVE_UP, 0);
   */
  MOVE_UP: {
    duration: ANIMATION_DURATIONS.SECTION.FADE_IN,
    ease: ANIMATION_EASES.POWER3_OUT,
    yPercent: 0,
  },

  /**
   * Standard move-up animation with in-out ease
   * @example tl.to(element, ANIMATION_CONFIGS.MOVE_UP, 0);
   */
  MOVE_UP_IN_OUT: {
    duration: ANIMATION_DURATIONS.ELEMENT.HOVER.DEFAULT,
    ease: ANIMATION_EASES.POWER3_IN_OUT,
    yPercent: 0,
  },

  /**
   * Fade in and move-up animation for quick view
   * @example tl.fromTo(element, { opacity: 0, yPercent: 10 }, ANIMATION_CONFIGS.QUICK_VIEW_FADE_IN_MOVE_UP, 0);
   */
  QUICK_VIEW_FADE_IN_MOVE_UP: {
    duration: ANIMATION_DURATIONS.ELEMENT.QUICK_VIEW.FADE_IN_MOVE_UP,
    ease: ANIMATION_EASES.SINE_OUT,
    opacity: 1,
    yPercent: 0,
  },

  /**
   * Fade in and move-up animation for quick view
   * @example tl.fromTo(element, { opacity: 1, yPercent: 0 }, ANIMATION_CONFIGS.QUICK_VIEW_FADE_OUT_MOVE_DOWN, 0);
   */
  QUICK_VIEW_FADE_OUT_MOVE_DOWN: {
    duration: ANIMATION_DURATIONS.ELEMENT.QUICK_VIEW.FADE_OUT_MOVE_DOWN,
    ease: ANIMATION_EASES.SINE_IN,
    opacity: 0,
    yPercent: 10,
  },

  /**
   * Scale-up animation for growing elements
   * @example tl.to(element, ANIMATION_CONFIGS.SCALE_UP, 0);
   */
  SCALE_UP: {
    duration: ANIMATION_DURATIONS.SECTION.FADE_IN,
    ease: ANIMATION_EASES.POWER3_OUT,
    scale: 1,
  },

  /**
   * Standard fade-in animation with stagger
   * @example tl.to(elements, ANIMATION_CONFIGS.STAGGERED_FADE_IN, 0);
   */
  STAGGERED_FADE_IN: {
    duration: ANIMATION_DURATIONS.SECTION.FADE_IN,
    ease: ANIMATION_EASES.SINE_OUT,
    opacity: 1,
    stagger: 0.15,
  },

  /**
   * Standard fade-in animation with stagger
   * @example tl.to(elements, ANIMATION_CONFIGS.STAGGERED_FADE_IN_SUBTLER, 0);
   */
  STAGGERED_FADE_IN_SUBTLER: {
    duration: ANIMATION_DURATIONS.SECTION.FADE_IN,
    ease: ANIMATION_EASES.SINE_OUT,
    opacity: 1,
    stagger: 0.05,
  },

  /**
   * Standard move-up animation with stagger
   * @example tl.to(elements, ANIMATION_CONFIGS.STAGGERED_MOVE_UP, 0);
   */
  STAGGERED_MOVE_UP: {
    duration: ANIMATION_DURATIONS.SECTION.FADE_IN,
    ease: ANIMATION_EASES.POWER3_OUT,
    stagger: 0.15,
    y: 0,
  },
};
